/**
 * The two things that decide whether a wide table can actually be read.
 *
 * Every DataGrid in BEST was `autoHeight`, which makes the grid as tall as its
 * rows and hands the scrolling to the PAGE. A DataGrid scrolls sideways at its
 * own bottom edge, so under `autoHeight` the horizontal scrollbar sits below
 * fifty rows of table: to move the columns you first scroll to the foot of the
 * page, and once you are there the header you are scrolling is off screen.
 * Bounding the height instead gives the grid its own viewport, and the sideways
 * scrollbar lands where the table ends ON SCREEN.
 *
 * The other half is wrapping. A DataGrid cell is `nowrap` with an ellipsis by
 * default, so a requirement statement - the whole point of the row - reads as
 * its first six words. Wrapping without bounding the height would have made the
 * first problem worse, which is why the two ship together.
 *
 * Both are here rather than in one screen because four grids had the same
 * defect: Requirements, the generic entity listing, and the two traceability
 * matrices. Issue #26 names the requirements table; it is not the requirements
 * table's bug.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import type { SxProps, Theme } from '@mui/material/styles'

/**
 * Room left below the table, so it does not sit flush against the window edge.
 *
 * Matches the page's own bottom padding in Layout (`p: 3` at md, 24px), so a
 * bounded table ends where any other block of content on the page would.
 */
const GUTTER = 24

/**
 * Never shorter than this, however little room is left.
 *
 * A short window would otherwise compute a table a few pixels tall, which is a
 * worse answer than one the page has to scroll to reach: at that point the
 * page's own scrollbar is the right tool and the grid should just be a normal
 * tall block.
 */
const MIN_HEIGHT = 320

/**
 * What jsdom and a not-yet-laid-out container get.
 *
 * A measured height needs a real box, and the first render has none - the ref
 * is not attached yet. Returning a plausible height rather than 0 means the
 * grid is never rendered into nothing, which is the one failure that would be
 * invisible in a unit test and obvious in a browser.
 */
const UNMEASURED = 560

/**
 * A height that ends the table at the bottom of the window.
 *
 * Measured rather than written down. A `calc(100vh - 260px)` would be right for
 * exactly one screen: Requirements carries a filter bar, Traceability a search
 * box, VerificationTraceability a legend and a selector, so each grid starts at
 * a different offset. Measuring the container's own top asks the question the
 * screen actually has, and re-asks it when the window changes or the chrome
 * above the table grows.
 *
 * @returns a ref to put on the grid's container, and the height for it
 */
export function useFittedHeight() {
  const ref = useRef<HTMLDivElement | null>(null)
  const [height, setHeight] = useState(UNMEASURED)

  const measure = useCallback(() => {
    const node = ref.current
    if (!node) return
    const room = window.innerHeight - node.getBoundingClientRect().top - GUTTER
    setHeight(room > MIN_HEIGHT ? room : MIN_HEIGHT)
  }, [])

  useEffect(() => {
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [measure])

  return { ref, height }
}

/**
 * Cells and headers that wrap instead of truncating.
 *
 * `alignItems: flex-start` and the vertical padding are not taste: MUI measures
 * an auto-height row from the cell's content box, and a cell that still
 * centres its content in a fixed line box reports the wrong height, so rows
 * overlap. They go together with `getRowHeight` below.
 *
 * `wordBreak` catches the one thing wrapping alone does not: a qualified name
 * with no spaces in it - `Requirements::SystemRequirements::SYS_OPS_3` - is a
 * single word as far as the browser is concerned and would push the column
 * wide again.
 */
export const WRAPPING_GRID_SX: SxProps<Theme> = {
  border: 0,
  '& .MuiDataGrid-cell': {
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    lineHeight: 1.45,
    alignItems: 'flex-start',
    py: 0.75,
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    whiteSpace: 'normal',
    lineHeight: 1.3,
  },
}

/**
 * The props that turn a fixed-height truncating grid into a wrapping one.
 *
 * Spread into the DataGrid alongside `WRAPPING_GRID_SX`; separate from it only
 * because one is styling and the other is grid configuration.
 *
 * `getRowHeight` is what actually lets a row grow - wrapping CSS on a fixed
 * 36px row would hide the second line rather than show it.
 */
export const WRAPPING_GRID_PROPS = {
  getRowHeight: () => 'auto' as const,
}
